import { useCallback, useEffect, useMemo, useState } from "react";
import { CreateModeSelector } from "./CreateModeSelector";
import resumeApi from "@/api/resume";
import { Modal } from "@/components/Modal/Modal";
import { ModalFooter } from "@/components/Modal/ModalFooter";
import { ImportLoading } from "@/components/Modals/NewResumeModal/Steps/ImportLoading";
import { SmartMatchSummary } from "@/components/Modals/SmartMatch/SmartMatchSummary";
import ResumePreviewGrid from "@/components/ResumePreviewGrid";
import { AppText } from "@/components/Text/AppText";
import { Input } from "@/components/form/Input";
import { translate } from "@/helper/translate";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { useContentEditor } from "@/hooks/useContentEditor";
import { useTransaction } from "@/hooks/useTransaction";
import { setTemplateLoadingState } from "@/store/app/slice";
import { getIsModalOpen, getModalProps } from "@/store/modal/selectors";
import { closeModal, openModal } from "@/store/modal/slice";
import { buildInitialPages, resetPages } from "@/store/pages/slice";
import { getResumesWithDates } from "@/store/resume/selectors";
import { setActiveResume } from "@/store/resume/slice";
import { getUserReachedResumeLimit } from "@/store/user/selectors";
import { setUserCredits } from "@/store/user/slice";
import { getUserJobById } from "@/store/userJob/selectors";
import { updateUserJob } from "@/store/userJob/slice";
import { TemplateLoadingState } from "@/types/app";
import { CreditActionCode } from "@/types/creditAction";
import { ModalTypes } from "@/types/modal";
import { IUserJob } from "@/types/userJob";
import { getIsResumeBuilderPage } from "@/utils/routes";
import { trackPostHogEvent } from "@/utils/tracking";

interface SmartMatchModalProps {
    userJobId: string;
    resumeId?: string;
}

type Step = "SELECT_RESUME" | "CONFIRM_DETAILS" | "PROCESSING";

const SmartMatchModal = () => {
    const isOpen = useAppSelector(getIsModalOpen(ModalTypes.SMART_MATCH));
    const modalProps = useAppSelector(
        getModalProps(ModalTypes.SMART_MATCH),
    ) as SmartMatchModalProps;
    const dispatch = useAppDispatch();
    const [currentStep, setCurrentStep] = useState<Step>("SELECT_RESUME");
    const [selectedResumeId, setSelectedResumeId] = useState<string | null>(
        modalProps?.resumeId || null,
    );
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [createMode, setCreateMode] = useState<"new" | "update">("new");
    const [resumeName, setResumeName] = useState<string>("");
    const { getCreditActionByCode, getCanAffordCreditAction } =
        useTransaction();
    const smartMatchAction = useMemo(() => {
        return getCreditActionByCode(CreditActionCode.SMART_MATCH);
    }, [getCreditActionByCode]);

    const canAffordSmartMatch = useMemo(() => {
        return getCanAffordCreditAction(smartMatchAction?.code ?? "");
    }, [getCanAffordCreditAction, smartMatchAction]);

    const userReachedResumeLimit = useAppSelector(getUserReachedResumeLimit);

    const userResumes = useAppSelector(getResumesWithDates);
    const userJob = useAppSelector(state =>
        getUserJobById(state, modalProps?.userJobId),
    );
    const { destroyEditors } = useContentEditor();
    const isResumeBuilderPage = getIsResumeBuilderPage();

    const selectedResume = userResumes?.find(r => r.id === selectedResumeId);

    const handleCancel = useCallback(() => {
        dispatch(closeModal(ModalTypes.SMART_MATCH));
    }, [dispatch]);

    useEffect(() => {
        if (modalProps?.resumeId) {
            setSelectedResumeId(modalProps.resumeId);
            setCurrentStep("CONFIRM_DETAILS");
        }
    }, [modalProps?.resumeId]);

    // Suggest a default resume name for the confirm screen, capped at 50 chars
    useEffect(() => {
        if (currentStep !== "CONFIRM_DETAILS") return;
        if (resumeName && resumeName.length > 0) return;

        const userJobTitle = userJob?.jobTitle || "New Resume";
        const suggestedBase = selectedResume?.name
            ? `${selectedResume.name} - ${userJobTitle}`
            : userJobTitle;

        const MAX_LEN = 50;
        const truncated =
            suggestedBase.length > MAX_LEN
                ? `${suggestedBase.slice(0, MAX_LEN).trimEnd()}`
                : suggestedBase;
        setResumeName(truncated);
    }, [currentStep, selectedResume?.name, userJob?.jobTitle, resumeName]);

    const handleNext = useCallback(() => {
        const handleSmartMatch = async () => {
            if (!selectedResumeId || !userJob) return;

            if (userReachedResumeLimit && createMode === "new") {
                dispatch(
                    openModal({
                        modalType: ModalTypes.DOCUMENT_LIMIT,
                    }),
                );
                setIsProcessing(false);
                return;
            }

            setIsProcessing(true);
            setError(null);

            try {
                const { resumeId, userUpdates } = await resumeApi.smartMatch({
                    resumeId: selectedResumeId,
                    jobId: userJob.id,
                    createMode,
                    name: resumeName.trim(),
                });
                if (userUpdates.credits) {
                    dispatch(setUserCredits(userUpdates.credits));
                }

                dispatch(closeModal(ModalTypes.SMART_MATCH));

                trackPostHogEvent("smart_match", {
                    action_taken: "running_smart_match",
                    create_mode: createMode,
                });

                if (isResumeBuilderPage) {
                    dispatch(
                        setTemplateLoadingState(TemplateLoadingState.LOADING),
                    );
                    dispatch(resetPages());
                    dispatch(buildInitialPages());
                    const updatedResume = await resumeApi.getResume(resumeId);
                    dispatch(setActiveResume(updatedResume));
                }

                destroyEditors();

                setTimeout(() => {
                    dispatch(
                        setTemplateLoadingState(TemplateLoadingState.READY),
                    );
                }, 1000);

                dispatch(
                    openModal({
                        modalType: ModalTypes.CREDITS_SUCCESS,
                        props: {
                            mode: "smart-match",
                            newResumeId: resumeId,
                            jobTitle: userJob.jobTitle,
                        },
                    }),
                );

                const userJobWithNewResume: IUserJob = {
                    ...userJob,
                    linkedResumeIds: userJob.linkedResumeIds?.concat(
                        resumeId,
                    ) || [resumeId],
                };
                dispatch(updateUserJob(userJobWithNewResume));
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : "An error occurred during the smart match process",
                );
                setCurrentStep("CONFIRM_DETAILS");
            } finally {
                setIsProcessing(false);
            }
        };

        if (currentStep === "SELECT_RESUME") {
            trackPostHogEvent("smart_match", {
                action_taken: "select_resume_clicked",
            });
            setCurrentStep("CONFIRM_DETAILS");
        } else if (currentStep === "CONFIRM_DETAILS") {
            trackPostHogEvent("smart_match", {
                action_taken: "confirm_details_clicked",
            });
            setCurrentStep("PROCESSING");

            handleSmartMatch();
        }
    }, [
        currentStep,
        selectedResumeId,
        userJob,
        isResumeBuilderPage,
        dispatch,
        createMode,
        resumeName,
        destroyEditors,
        userReachedResumeLimit,
    ]);

    const handleBack = useCallback(() => {
        if (currentStep === "CONFIRM_DETAILS") {
            setCurrentStep("SELECT_RESUME");
        }
    }, [currentStep]);

    const getPrimaryActionText = () => {
        switch (currentStep) {
            case "SELECT_RESUME":
                return "Next";
            case "CONFIRM_DETAILS":
                return createMode === "new"
                    ? "Create New Resume"
                    : "Update Existing Resume";
            case "PROCESSING":
                return "Processing...";
            default:
                return "Confirm";
        }
    };

    const getPrimaryActionDisabled = () => {
        switch (currentStep) {
            case "SELECT_RESUME":
                return !selectedResumeId;
            case "PROCESSING":
                return !canAffordSmartMatch;
            default:
                return false;
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case "SELECT_RESUME":
                return (
                    <div>
                        <AppText
                            variant="regular"
                            className="mb-4 text-neutral-700"
                        >
                            Choose a resume to tailor with{" "}
                            <AppText
                                variant="regular"
                                applyGradient
                                className="!font-medium"
                                as="span"
                            >
                                Smart Match
                            </AppText>
                            . Our AI will create a {translate("optimised")}{" "}
                            version to better match the job you&apos;re applying
                            for
                        </AppText>
                        <div className="flex h-full max-h-[100%] flex-col lg:h-[60vh] lg:max-h-[500px]">
                            <div className="no-scrollbar h-full overflow-y-auto px-4">
                                <ResumePreviewGrid
                                    resumes={userResumes}
                                    selectedResumeIds={
                                        selectedResumeId
                                            ? [selectedResumeId]
                                            : []
                                    }
                                    setSelectedResumeIds={(
                                        selectedResumes: string[],
                                    ) =>
                                        setSelectedResumeId(selectedResumes[0])
                                    }
                                />
                            </div>
                        </div>
                    </div>
                );
            case "CONFIRM_DETAILS":
                return (
                    <div className="flex flex-col space-y-6">
                        <AppText
                            variant="regular"
                            className="text-neutral-700"
                        >
                            Review the details below. Choose whether to create a
                            new resume or update the existing one with the job
                            listing to create an {translate("optimised")}{" "}
                            resume.
                        </AppText>
                        <SmartMatchSummary
                            selectedResume={selectedResume}
                            userJob={userJob}
                        />
                        {error && (
                            <div className="rounded-md bg-red-50 p-4">
                                <AppText
                                    variant="regular"
                                    className="text-red-600"
                                >
                                    {error}
                                </AppText>
                            </div>
                        )}
                        <Input
                            label="Tailored Resume Name"
                            placeholder="Enter resume name"
                            showCounter
                            maxLength={50}
                            defaultValue={resumeName}
                            onChange={(_, value) => setResumeName(value)}
                            containerClassName="w-full"
                        />
                        <CreateModeSelector
                            createMode={createMode}
                            setCreateMode={setCreateMode}
                        />
                    </div>
                );
            case "PROCESSING":
                return (
                    <div className="flex min-h-[200px] flex-1 flex-col items-center justify-center gap-2">
                        <ImportLoading importError={error || ""} />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <Modal
            modalType={ModalTypes.SMART_MATCH}
            open={isOpen}
            afterClose={() => {
                setSelectedResumeId(null);
                setError(null);
                setCurrentStep("SELECT_RESUME");
                setIsProcessing(false);
            }}
            closeOnOverlayClick={currentStep !== "PROCESSING"}
            noHeader={currentStep === "PROCESSING"}
            gradientBackground={currentStep === "PROCESSING"}
            title={`Tailor Resume for ${userJob?.jobTitle}`}
            width="4xl"
            mobileFullScreen={true}
            footer={
                currentStep === "PROCESSING" ? null : (
                    <ModalFooter
                        primaryActionText={getPrimaryActionText()}
                        primaryActionOnClick={handleNext}
                        primaryActionDisabled={getPrimaryActionDisabled()}
                        primaryActionLoading={isProcessing}
                        secondaryActionText={
                            currentStep === "CONFIRM_DETAILS"
                                ? undefined
                                : "Cancel"
                        }
                        secondaryActionOnClick={
                            currentStep === "CONFIRM_DETAILS"
                                ? handleBack
                                : handleCancel
                        }
                        creditAction={
                            currentStep === "CONFIRM_DETAILS"
                                ? smartMatchAction
                                : undefined
                        }
                        thirdActionText={
                            currentStep === "CONFIRM_DETAILS" &&
                            !modalProps?.resumeId
                                ? "Back"
                                : undefined
                        }
                        thirdActionOnClick={handleBack}
                    />
                )
            }
        >
            {renderStepContent()}
        </Modal>
    );
};

export default SmartMatchModal;
